import { assert_string_not_empty } from '@shared/asserts/utils_asserts';
import { DTO__color } from '@shared/dto/+image/image_asset_base/DTO__color';

export function number_to_hex(n: number): string {
  if (n < 0 || n >= 100) throw new RangeError('Number must be in 0..99');

  // Golden angle in degrees
  const goldenAngle = 137.508;
  const hue = (n * goldenAngle) % 360;

  // Tune these for brightness/saturation as needed
  const saturation = 85; // %
  const value = 95; // %

  return hsv_to_hex(hue, saturation, value);
}

function hsv_to_hex(h: number, s: number, v: number): string {
  s /= 100;
  v /= 100;

  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function invert_hex(hex: string): string {
  // 1. Remove the hash if it exists
  if (hex.startsWith('#')) hex = hex.slice(1);

  // 2. Convert 3-digit hex to 6-digit hex (e.g., "03F" -> "0033FF")
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }

  // 3. Parse the r, g, b values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // 4. Invert each channel and convert back to hex
  const invert = (channel: number) => {
    const inverted = (255 - channel).toString(16);
    return inverted.padStart(2, '0'); // Ensure it's always 2 digits
  };

  return `#${invert(r)}${invert(g)}${invert(b)}`;
}

export function hex_to_rgb(hex: string): DTO__color {
  assert_string_not_empty(hex);

  const normalized = hex.replace('#', '');
  const bigint = parseInt(normalized, 16);
  return DTO__color.create((bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255);
}
